import React, { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Card } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { useQuery } from '@tanstack/react-query';
import FrequencyBadgeImage from '@/components/FrequencyBadgeImage';
import VolumeBadgeImage from '@/components/VolumeBadgeImage';
import { getRotatingNotificationMessage } from '@shared/client-types';
import { resolveYesterdayBadges } from '@/lib/yesterdayBadges';
import { resolveDailyTargetMl, resolveFrequencyGoal } from '@/lib/hydrationGoals';
import { featuredArticlePath, resolveContentImageUrl } from '@/lib/contentImage';
import { pickFeaturedLearning } from '@/lib/demoArticle';
import {
  advertisementHref,
  journeyDayForMember,
  pickCohortAdvertisement,
} from '@shared/imageArticle';
import { subDays } from 'date-fns';

export default function NewsCarousel() {
  const [api, setApi] = useState<CarouselApi>();
  const [active, setActive] = useState(0);

  // Use locale date (YYYY-MM-DD). date-fns format() treats 'en-CA' as tokens and throws,
  // which previously crashed Mainstage with the full-page "Something went wrong" screen.
  const todayStr = new Date().toLocaleDateString('en-CA');
  const yesterdayStr = subDays(new Date(), 1).toLocaleDateString('en-CA');

  const { data: userProfile } = useQuery<any>({
    queryKey: ['/api/profile'],
    retry: false,
  });

  const { data: todayTracking } = useQuery<any>({
    queryKey: ['/api/onesignal/tracking', { date: todayStr }],
    retry: false,
  });

  const { data: messages } = useQuery<any[]>({
    queryKey: ['/api/content/messages'],
    retry: false,
  });

  const { data: learning } = useQuery<any[]>({
    queryKey: ['/api/content/learning'],
    retry: false,
  });

  const { data: yesterdayStats } = useQuery<any>({
    queryKey: ['/api/stats/daily', { date: yesterdayStr }],
    retry: false,
  });

  const { data: yesterdayTracking } = useQuery<any>({
    queryKey: ['/api/onesignal/tracking', { date: yesterdayStr }],
    retry: false,
  });

  useEffect(() => {
    if (!api) return;
    setActive(api.selectedScrollSnap());
    api.on('select', () => setActive(api.selectedScrollSnap()));
  }, [api]);

  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      const count = api.scrollSnapList().length;
      if (count <= 1) return;
      const next = (api.selectedScrollSnap() + 1) % count;
      api.scrollTo(next);
    }, 4000);
    return () => clearInterval(interval);
  }, [api]);

  const advertisement = pickCohortAdvertisement(
    Array.isArray(messages) ? messages : [],
    userProfile?.companyId
  );
  const advertisementImage = advertisement?.imageUrl?.trim() || '';
  const advertisementHrefValue = advertisementHref(advertisement?.linkUrl);

  const timezone = userProfile?.timezone || 'Asia/Kolkata';
  const reminderNumber = todayTracking?.reminderCount ?? 1;
  const rotationSalt = todayTracking?.successfulIntakes ?? 0;
  const rotationIndex = Math.max(0, reminderNumber + rotationSalt - 1);

  const notifAction = getRotatingNotificationMessage(reminderNumber, {
    timezone,
    rotationSalt,
  });

  const actionMessage =
    advertisement?.text?.trim() ||
    (Array.isArray(messages) && messages.length > 0
      ? messages[rotationIndex % messages.length].text
      : notifAction.text);

  const journeyDay = journeyDayForMember(userProfile?.profileCreatedAt || userProfile?.createdAt);
  const featured = pickFeaturedLearning(Array.isArray(learning) ? learning : [], journeyDay);
  const featuredImage = resolveContentImageUrl(featured);

  const fallbackTarget = resolveDailyTargetMl(userProfile);
  const { frequencyBadge, volumeBadge } = resolveYesterdayBadges({
    totalVolume: yesterdayStats?.totalVolume || 0,
    targetVolume: yesterdayStats?.targetVolume || fallbackTarget,
    missCount: yesterdayStats?.missCount ?? yesterdayTracking?.missCount ?? 0,
    successfulIntakes: yesterdayStats?.successfulReminders ?? yesterdayTracking?.successfulIntakes,
    expectedReminders: resolveFrequencyGoal(userProfile),
    totalReminders: yesterdayStats?.totalReminders ?? yesterdayTracking?.reminderCount,
    volumeBadgeName: yesterdayStats?.volumeBadgeName,
    frequencyBadgeName: yesterdayStats?.frequencyBadgeName,
    volumeBadgePercent: yesterdayStats?.volumeBadgePercent,
    badgesFinalized: yesterdayStats?.badgesFinalized,
    activeHoursStart: userProfile?.activeHoursStart,
    activeHoursEnd: userProfile?.activeHoursEnd,
    reminderInterval: userProfile?.reminderInterval,
  });

  const items: Array<{
    to: string;
    stickerBg: string;
    stickerBgBack: string;
    stickerText?: React.ReactNode;
    stickerImageSrc?: string;
    stickerImageAlt?: string;
    stickerKind?: 'frequency' | 'volume' | 'image';
    title: string;
    subtitle: string;
    bannerSrc?: string;
    bannerAlt?: string;
    external?: boolean;
  }> = [
    advertisementImage
      ? {
          to: advertisementHrefValue || '',
          external: Boolean(advertisementHrefValue?.startsWith('http')),
          stickerBg: 'bg-purple-600',
          stickerBgBack: 'bg-purple-500',
          bannerSrc: advertisementImage,
          bannerAlt: advertisement?.text?.trim() || 'Advertisement',
          title: '',
          subtitle: '',
        }
      : {
          to: '/dashboard',
          stickerBg: 'bg-purple-600',
          stickerBgBack: 'bg-purple-500',
          stickerText: (
            <>
              ACT
              <br />
              NOW
            </>
          ),
          title: 'ACTION ITEM',
          subtitle: actionMessage,
        },
    {
      to: featuredArticlePath(featured?.id),
      stickerBg: 'bg-lime-500',
      stickerBgBack: 'bg-lime-500/70',
      stickerText: featuredImage ? undefined : (
        <>
          READ
          <br />
          NOW
        </>
      ),
      stickerImageSrc: featuredImage || undefined,
      stickerImageAlt: featured?.headline || 'Featured',
      stickerKind: featuredImage ? 'image' : undefined,
      title: 'FEATURED ARTICLE',
      subtitle: featured?.headline || featured?.title || "Tap to read today's hydration content",
    },
    {
      to: '/analytics',
      stickerBg: 'bg-[#2538c7]',
      stickerBgBack: 'bg-[#2538c7]/60',
      // Prefer volume badge artwork when only one sticker fits.
      stickerImageSrc: volumeBadge.imageSrc,
      stickerImageAlt: volumeBadge.name,
      stickerKind: 'volume',
      title: "YESTERDAY'S BADGE",
      subtitle: `Yesterday: ${volumeBadge.name} · ${frequencyBadge.name} — see your Pulse recap`,
    },
  ];

  return (
    <div className="pt-2 overflow-visible">
      <Carousel setApi={setApi} className="w-full" opts={{ align: 'start' }}>
        <CarouselContent>
          {items.map((it, i) => {
            const card = it.bannerSrc ? (
              <Card className="bg-cream border-deep-100 rounded-3xl p-0 shadow-sm overflow-hidden h-[104px]">
                <img
                  src={it.bannerSrc}
                  alt={it.bannerAlt || 'Advertisement'}
                  className="h-full w-full object-cover"
                />
              </Card>
            ) : (
              <Card className="bg-cream border-deep-100 rounded-3xl p-3 shadow-sm flex items-center gap-3 overflow-hidden">
                <div className="relative w-20 h-20 shrink-0">
                  <div
                    className={`absolute inset-0 ${it.stickerBgBack} rounded-2xl rotate-[-6deg]`}
                  />
                  <div
                    className={`absolute inset-0 ${it.stickerBg} rounded-2xl flex items-center justify-center p-1.5 overflow-hidden`}
                  >
                    {it.stickerImageSrc ? (
                      it.stickerKind === 'volume' ? (
                        <VolumeBadgeImage
                          src={it.stickerImageSrc}
                          alt={it.stickerImageAlt || 'Volume badge'}
                          size="sm"
                          className="h-12 max-w-full"
                        />
                      ) : it.stickerKind === 'image' ? (
                        <img
                          src={it.stickerImageSrc}
                          alt={it.stickerImageAlt || ''}
                          className="h-full w-full object-contain rounded-xl bg-deep-100/40"
                        />
                      ) : (
                        <FrequencyBadgeImage
                          src={it.stickerImageSrc}
                          alt={it.stickerImageAlt || 'Frequency badge'}
                          size="sm"
                          className="h-12 max-w-full"
                        />
                      )
                    ) : (
                      <span className="text-white font-black text-sm leading-tight italic text-center px-1">
                        {it.stickerText}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-deep-800 tracking-wide truncate">
                    {it.title}
                  </p>
                  <p className="text-xs text-deep-500 mt-1 leading-snug line-clamp-2 break-words">
                    {it.subtitle}
                  </p>
                </div>
              </Card>
            );
            return (
              <CarouselItem key={i}>
                {it.bannerSrc && it.external ? (
                  <a href={it.to} target="_blank" rel="noopener noreferrer" className="block">
                    {card}
                  </a>
                ) : it.to ? (
                  <Link href={it.to} className="block">
                    {card}
                  </Link>
                ) : (
                  card
                )}
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      <div className="flex justify-center gap-2 mt-2">
        {items.map((_, i) => (
          <button
            key={i}
            onClick={() => api?.scrollTo(i)}
            className={`h-2 w-2 rounded-full transition-all ${i === active ? 'bg-purple-600' : 'bg-deep-200'}`}
            aria-label={`Message ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
